import React, { useState, useEffect } from 'react';
import { fieldOpsAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { MapPin, AlertTriangle, CheckCircle, Send, FileText } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  safe: { color: '#1F7A5A', label: 'Safe', icon: CheckCircle },
  assistance_needed: { color: '#9A6B1A', label: 'Assistance Needed', icon: AlertTriangle },
  emergency: { color: '#B3261E', label: '🚨 EMERGENCY', icon: AlertTriangle },
};

export default function AdminFieldOps() {
  const [checkins, setCheckins] = useState([]);
  const [showCheckin, setShowCheckin] = useState(false);
  const [showStory, setShowStory] = useState(false);
  const [checkinForm, setCheckinForm] = useState({ location_name: '', lat: '', lon: '', status: 'safe', message: '' });
  const [storyForm, setStoryForm] = useState({ title: '', content: '', location: '', photo_urls: [] });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fieldOpsAPI.listCheckins(true);
      setCheckins(r.data.checkins || []);
    } catch { toast.error('Failed to load check-ins'); }
    finally { setLoading(false); }
  };

  const submitCheckin = async () => {
    setSubmitting(true);
    try {
      await fieldOpsAPI.checkin(checkinForm);
      toast.success(`Check-in sent: ${checkinForm.status === 'emergency' ? '🚨 Emergency alert sent to editors' : 'You are marked as safe'}`);
      setShowCheckin(false);
      setCheckinForm({ location_name: '', lat: '', lon: '', status: 'safe', message: '' });
      load();
    } catch { toast.error('Failed to send check-in'); }
    finally { setSubmitting(false); }
  };

  const submitStory = async () => {
    setSubmitting(true);
    try {
      const r = await fieldOpsAPI.submitStory(storyForm);
      toast.success(`Field story submitted as draft (ID: ${r.data.article_id})`);
      setShowStory(false);
      setStoryForm({ title: '', content: '', location: '', photo_urls: [] });
    } catch { toast.error('Failed to submit story'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><MapPin className="w-6 h-6 text-[#B89B5E]" /> Field Operations</h1>
          <p className="text-sm text-[#5B616E] mt-1">Reporter safety check-ins and field story submission</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowStory(true)}><FileText className="w-4 h-4 mr-2" /> Submit Story</Button>
          <Button onClick={() => setShowCheckin(true)} className="bg-[#121316] text-white hover:bg-[#1B1D22]"><MapPin className="w-4 h-4 mr-2" /> Check In</Button>
        </div>
      </div>

      {/* Emergency alert */}
      {checkins.some(c => c.status === 'emergency') && (
        <div className="bg-red-50 border-2 border-red-400 rounded-xl p-4 flex items-center gap-3">
          <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0" />
          <p className="text-sm font-semibold text-red-700">
            🚨 Active emergency check-in from field reporter — see list below
          </p>
        </div>
      )}

      <Card className="bg-white border-[#E7E0D6]">
        <CardHeader><CardTitle className="text-sm">Active Check-ins (last 12h)</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-6 text-[#5B616E] text-sm">Loading…</div>
          ) : checkins.length === 0 ? (
            <div className="text-center py-8 text-[#5B616E]">
              <MapPin className="w-12 h-12 mx-auto mb-3 opacity-20" />
              <p>No active field check-ins in the last 12 hours</p>
            </div>
          ) : (
            <div className="space-y-2">
              {checkins.map(c => {
                const cfg = STATUS_CONFIG[c.status] || STATUS_CONFIG.safe;
                const Icon = cfg.icon;
                return (
                  <div key={c.id} className="flex items-center justify-between p-3 border rounded-lg" style={{ borderColor: `${cfg.color}30`, background: `${cfg.color}05` }}>
                    <div className="flex items-center gap-3">
                      <Icon className="w-5 h-5 flex-shrink-0" style={{ color: cfg.color }} />
                      <div>
                        <p className="text-sm font-medium">{c.user_email}</p>
                        <p className="text-xs text-[#5B616E]"><MapPin className="w-3 h-3 inline mr-0.5" />{c.location_name}</p>
                        {c.message && <p className="text-xs italic text-[#5B616E]">"{c.message}"</p>}
                        <p className="text-[10px] text-[#5B616E]">{c.checkin_at ? new Date(c.checkin_at).toLocaleString() : ''}</p>
                      </div>
                    </div>
                    <Badge style={{ background: `${cfg.color}20`, color: cfg.color }} className="text-[10px]">{cfg.label}</Badge>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Check-in dialog */}
      <Dialog open={showCheckin} onOpenChange={setShowCheckin}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><MapPin className="w-4 h-4" /> Safety Check-in</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium mb-1 block">Status *</label>
              <Select value={checkinForm.status} onValueChange={v => setCheckinForm(f => ({ ...f, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="safe">✅ Safe</SelectItem>
                  <SelectItem value="assistance_needed">⚠️ Assistance Needed</SelectItem>
                  <SelectItem value="emergency">🚨 EMERGENCY</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Input placeholder="Location name (city, building, area)" value={checkinForm.location_name} onChange={e => setCheckinForm(f => ({ ...f, location_name: e.target.value }))} />
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Latitude (optional)" type="number" value={checkinForm.lat} onChange={e => setCheckinForm(f => ({ ...f, lat: e.target.value }))} />
              <Input placeholder="Longitude" type="number" value={checkinForm.lon} onChange={e => setCheckinForm(f => ({ ...f, lon: e.target.value }))} />
            </div>
            <Textarea rows={3} placeholder="Message to editors (optional)" value={checkinForm.message} onChange={e => setCheckinForm(f => ({ ...f, message: e.target.value }))} />
            {checkinForm.status === 'emergency' && (
              <p className="text-xs text-red-600 bg-red-50 p-2 rounded">An emergency alert will be sent to all editors immediately.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCheckin(false)}>Cancel</Button>
            <Button onClick={submitCheckin} disabled={submitting} className={`${checkinForm.status === 'emergency' ? 'bg-red-600 hover:bg-red-700' : 'bg-[#121316] hover:bg-[#1B1D22]'} text-white`}>
              <Send className="w-4 h-4 mr-1" /> {submitting ? 'Sending…' : 'Send Check-in'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Field story dialog */}
      <Dialog open={showStory} onOpenChange={setShowStory}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Submit Field Story</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Input placeholder="Story headline *" value={storyForm.title} onChange={e => setStoryForm(f => ({ ...f, title: e.target.value }))} />
            <Textarea rows={6} placeholder="Story content / field notes..." value={storyForm.content} onChange={e => setStoryForm(f => ({ ...f, content: e.target.value }))} />
            <Input placeholder="Location" value={storyForm.location} onChange={e => setStoryForm(f => ({ ...f, location: e.target.value }))} />
            <Input placeholder="Photo URLs (comma-separated)" onChange={e => setStoryForm(f => ({ ...f, photo_urls: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))} />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowStory(false)}>Cancel</Button>
            <Button onClick={submitStory} disabled={submitting} className="bg-[#121316] text-white hover:bg-[#1B1D22]">
              <FileText className="w-4 h-4 mr-1" /> {submitting ? 'Submitting…' : 'Submit Story'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
